import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SLOTS } from "@/components/PageView";
import FlipBook from "@/components/FlipBook";

const U = (id, w = 900) => `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=80&w=${w}`;
const P = {
  coupleLights: U("1722952934708-749c22eb2e58"),
  hennaDark: U("1621801306185-8c0ccf9c8eb8"),
  hennaBlue: U("1780326761884-04dedf3778f8"),
  brideFlowers: U("1587271315307-eaebc181c749"),
  brideVeil: U("1610173827043-9db50e0d8ef9"),
  groom: U("1781106699853-db5cad3a5dbd"),
  handsRitual: U("1597157639073-69284dc0fdaf"),
  kalash: U("1771769076330-c424547f5c1c"),
  mandap: U("1587271636175-90d58cdad458"),
  varmala: U("1726068449701-4e11c5d64b11"),
  family: U("1610173827002-62c0f1f05d04"),
  ballroom: U("1587271407850-8d438ca9fdf2"),
  beach: U("1774814328122-e6006f0632e6"),
  river: U("1630526720753-aa4e71acf67d"),
  woods: U("1735052712464-9d24b69be5f5"),
  babyEat: U("1491013516836-7db643ee125a"),
  babyFloat: U("1519689680058-324335c77eba"),
  babyBear: U("1522771930-78848d9293e8"),
  babyFeet: U("1555252333-9f8e92e65df9"),
  babyFamily: U("1476703993599-0035a21b17a9"),
  babyToys: U("1515488042361-ee00e0ddd4e4"),
  hawaMahal: U("1477587458883-47145ed94245"),
  houseboat: U("1506461883276-594a12b11cf3"),
  goa: U("1512343879784-a960bf40e7f2"),
  taj: U("1524492412937-b28074a5d7da"),
  tajArch: U("1548013146-72479768bada"),
  gateway: U("1587474260584-136574528ed5"),
  marineDrive: U("1570168007204-dfb528c6958f"),
  ladakhMonastery: U("1544735716-392fe2489ffa"),
  manali: U("1602216056096-3b40cc0c9944"),
  snowTrek: U("1567157577867-05ccb1388e66"),
  kerala2: U("1626621341517-bbf3d9990a23"),
};

export const SAMPLE_BOOKS = {
  wedding: {
    label: "Wedding", theme: "theme-heritage", accent: "#C85A32", coverBg: "#272422",
    cover: { photo: P.coupleLights, title: "Ananya & Rohan", sub: "Jaipur · November 2025" },
    title: { mark: "✦", title: "Ananya & Rohan", sub: "Seven steps, one forever", overline: "Three days in Jaipur" },
    blurb: "Ananya & Rohan's three days in Jaipur — mehendi to reception — curated, captioned and laid out the way we do it.",
    meta: "Square 21×21 cm · Heritage theme",
    pages: [
      { layout: "two", photos: [P.hennaDark, P.hennaBlue], caption: "Mehendi ki raat — the colour deepened, and so did the laughter" },
      { layout: "caption", photos: [P.brideFlowers], caption: "Ananya, a breath before the veil" },
      { layout: "full", photos: [P.brideVeil] },
      { layout: "caption", photos: [P.groom], caption: "Rohan, waiting for the dhol to start" },
      { layout: "two", photos: [P.handsRitual, P.kalash], caption: "Baraat, blessings and a hundred hugs" },
      { layout: "full", photos: [P.mandap] },
      { layout: "three", photos: [P.varmala, P.family, P.ballroom], caption: "Saat phere. Seven promises, one evening." },
      { layout: "caption", photos: [P.beach], caption: "Danced till the band gave up" },
      { layout: "caption", photos: [P.coupleLights], caption: "Under a thousand fairy lights" },
      { layout: "two", photos: [P.river, P.woods], caption: "Just the two of us, finally" },
    ],
  },
  baby: {
    label: "Baby's first year", theme: "theme-baby", accent: "#B5553D", coverBg: "#5E5868",
    cover: { photo: P.babyBear, title: "Aarav", sub: "The first 365 days" },
    title: { mark: "❀", title: "Aarav", sub: "Small hands, big year", overline: "Month one to month twelve" },
    blurb: "Aarav's first year — from tiny feet to first steps — in the soft Little Ones theme, captions and all.",
    meta: "Square 21×21 cm · Little Ones theme",
    pages: [
      { layout: "caption", photos: [P.babyFeet], caption: "Day 3. Ten toes, already running the house." },
      { layout: "full", photos: [P.babyEat] },
      { layout: "caption", photos: [P.babyFloat], caption: "Month 5. First swim — the pool lost." },
      { layout: "two", photos: [P.babyToys, P.babyFamily], caption: "Month 8. Toys everywhere, Nani most of all." },
      { layout: "caption", photos: [P.babyBear], caption: "Month 12. One candle, one roar." },
    ],
  },
  travel: {
    label: "Travel journal", theme: "theme-travel", accent: "#4F7A5C", coverBg: "#1E1B24",
    cover: { photo: P.ladakhMonastery, title: "Bharat, by road", sub: "Kerala to Ladakh · 41 days" },
    title: { mark: "➳", title: "Bharat, by road", sub: "forty-one days. eleven states. one car.", overline: "a travel journal" },
    blurb: "Forty-one days from Kerala's backwaters to Ladakh's passes — laid out in the Travel Journal theme with typewriter notes.",
    meta: "Landscape A4 · Travel Journal theme",
    pages: [
      { layout: "two", photos: [P.houseboat, P.kerala2], caption: "day 1 — alleppey. the houseboat moved slower than us." },
      { layout: "caption", photos: [P.goa], caption: "day 6 — goa. we said one night. we stayed four." },
      { layout: "three", photos: [P.gateway, P.marineDrive, P.taj], caption: "days 12–19 — bombay, then the long road to agra." },
      { layout: "full", photos: [P.tajArch] },
      { layout: "caption", photos: [P.hawaMahal], caption: "day 23 — jaipur. pink city, pinker sunburn." },
      { layout: "two", photos: [P.manali, P.snowTrek], caption: "day 34 — manali to leh. the car coughed; we cheered." },
      { layout: "caption", photos: [P.ladakhMonastery], caption: "day 41 — thiksey. we ran out of road, not reasons." },
    ],
  },
};

const assemble = (b) => {
  const body = b.pages.filter((p) => !p.type);
  const pages = [{ type: "cover" }, { type: "title" }, ...body];
  if (body.length % 2 === 1) pages.push({ layout: "caption", photos: [b.cover.photo], caption: "Until the next one." });
  pages.push({ type: "cta" }, { type: "back" });
  return pages;
};

const fs = (min, cq, max) => ({ fontSize: `clamp(${min}px, ${cq}cqw, ${max}px)` });

function Cover({ b }) {
  return (
    <div className="absolute inset-0" style={{ background: b.coverBg }} data-testid="sample-cover">
      <img src={b.cover.photo} alt={b.cover.title} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-[6%] text-center text-[#F9F6F0]">
        <h3 className="font-serif leading-tight tracking-tight" style={fs(16, 5.2, 52)}>{b.cover.title}</h3>
        <p className="mt-[2%] font-serif italic opacity-90" style={fs(8, 1.9, 19)}>{b.cover.sub}</p>
      </div>
    </div>
  );
}

function TitlePage({ b }) {
  return (
    <div className={`${b.theme} absolute inset-0 flex flex-col items-center justify-center text-center text-[#272422]`} data-testid="sample-title-page">
      <span style={{ color: b.accent, ...fs(10, 2.4, 24) }}>{b.title.mark}</span>
      <h3 className="mt-[4%] font-serif leading-tight" style={fs(14, 4.2, 42)}>{b.title.title}</h3>
      <p className="cap mt-[2%] font-serif italic text-[#5C5650]" style={fs(7, 1.8, 18)}>{b.title.sub}</p>
      <p className="overline mt-[8%]" style={fs(5, 1.1, 11)}>{b.title.overline}</p>
    </div>
  );
}

function CtaPage({ b }) {
  return (
    <div className={`${b.theme} absolute inset-0 flex flex-col items-center justify-center px-[10%] text-center`} data-testid="sample-cta-page">
      <p className="overline" style={fs(5, 1.1, 11)}>The end — of this one</p>
      <h3 className="mt-[4%] font-serif leading-tight" style={fs(14, 3.8, 38)}>Your turn.</h3>
      <p className="mt-[3%] text-[#5C5650]" style={fs(7, 1.6, 15)}>Upload your photos. We'll do the rest — you fix whatever you like.</p>
      <Link to="/register" data-testid="sample-make-yours-btn" onClick={(e) => e.stopPropagation()}
        className="btn-primary mt-[6%] !px-[6%] !py-[3%]" style={fs(7, 1.6, 14)}>
        Make yours <ArrowRight style={{ width: "1.1em", height: "1.1em" }} />
      </Link>
    </div>
  );
}

function BackCover() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#272422] text-center text-[#F9F6F0]/70" data-testid="sample-back-cover">
      <p className="font-serif tracking-tight text-[#F9F6F0]" style={fs(10, 2.6, 26)}>Click, Print & Doodle</p>
      <p className="overline mt-[3%] !text-[#F9F6F0]/50" style={fs(5, 1, 10)}>Printed in India · 300 DPI</p>
    </div>
  );
}

function PhotoPage({ page, b }) {
  const full = page.layout === "full";
  const rects = SLOTS[page.layout];
  const pad = full ? 0 : 7;
  const capBelow = page.caption && !full && page.layout !== "caption";
  return (
    <div className={`${b.theme} absolute inset-0`}>
      <div className="absolute" style={{ inset: `${pad}% ${pad}% ${capBelow ? pad + 6 : pad}% ${pad}%` }}>
        {rects.map(([x, y, w, h], i) => (
          <div key={i} className={`absolute overflow-hidden bg-[#E9E1D2] ${full ? "" : "rounded-[2px]"}`}
            style={{ left: `${x * 100}%`, top: `${y * 100}%`, width: `${w * 100}%`, height: `${h * 100}%` }}>
            <img src={page.photos[i]} alt={page.caption || b.label} draggable={false} loading="lazy"
              className="h-full w-full object-cover" style={{ objectPosition: "50% 40%" }} />
          </div>
        ))}
        {page.layout === "caption" && (
          <p className="cap absolute bottom-0 left-0 right-0 flex h-[15%] items-center justify-center px-[4%] text-center text-[#5C5650]" style={fs(6, 1.7, 17)}>{page.caption}</p>
        )}
      </div>
      {capBelow && <p className="cap absolute bottom-[3.5%] left-0 right-0 px-[7%] text-center text-[#5C5650]" style={fs(6, 1.5, 15)}>{page.caption}</p>}
    </div>
  );
}

function SamplePage({ page, b }) {
  if (page.type === "cover") return <Cover b={b} />;
  if (page.type === "title") return <TitlePage b={b} />;
  if (page.type === "cta") return <CtaPage b={b} />;
  if (page.type === "back") return <BackCover />;
  return <PhotoPage page={page} b={b} />;
}

export default function SampleBook({ book = "wedding" }) {
  const b = SAMPLE_BOOKS[book];
  return <FlipBook key={book} id="sample" autoOpen pages={assemble(b).map((p, i) => <SamplePage key={i} page={p} b={b} />)} />;
}
